import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Mail, Plus, Users, Send } from "lucide-react";
import { TierBadge, TIER_META } from "@/components/Bits";

function NewCampaignDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tier, setTier] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name) return toast.error("Name required");
    setBusy(true);
    try {
      const res = await api.post("/campaigns", {
        name,
        description,
        target_tier: tier || null,
        steps: [],
        require_approval: true,
      });
      toast.success("Campaign created");
      onCreated && onCreated(res.data);
      setOpen(false);
      setName(""); setDescription(""); setTier("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[#1B4332] hover:bg-[#2D5A47] rounded-sm text-white" data-testid="new-campaign-button">
          <Plus className="h-4 w-4 mr-1.5" /> New Campaign
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="new-campaign-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">New campaign</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="input-campaign-name" />
          </div>
          <div>
            <Label>Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="input-campaign-description" />
          </div>
          <div>
            <Label>Target tier</Label>
            <Select value={tier} onValueChange={setTier}>
              <SelectTrigger data-testid="input-campaign-tier"><SelectValue placeholder="Any tier" /></SelectTrigger>
              <SelectContent>
                {Object.entries(TIER_META).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#1B4332] hover:bg-[#2D5A47] rounded-sm" data-testid="submit-new-campaign">
            {busy ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Campaigns() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/campaigns");
      setItems(res.data.items);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="campaigns-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="uppercase-tiny text-gray-500">Outreach</div>
          <h1 className="font-heading text-4xl tracking-tight mt-1">Campaigns</h1>
          <p className="text-sm text-gray-600 mt-2">Email sequences your team runs to convert leads into partners.</p>
        </div>
        <NewCampaignDialog onCreated={load} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="campaigns-grid">
        {loading && <div className="text-sm text-gray-500">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="col-span-full text-center py-12 border border-dashed border-gray-300 rounded-sm text-gray-500">
            No campaigns yet. Create your first one to kick off outreach.
          </div>
        )}
        {items.map((c) => (
          <Link to={`/campaigns/${c.campaign_id}`} key={c.campaign_id}
            className={`block bg-white border border-gray-200 rounded-sm p-5 hover:-translate-y-0.5 hover:shadow-sm transition-transform duration-200 ${c.target_tier ? `tier-border-${c.target_tier}` : ""}`}
            data-testid={`campaign-card-${c.campaign_id}`}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <Mail className="h-4 w-4 text-[#1B4332]" />
              <TierBadge tier={c.target_tier} />
            </div>
            <div className="font-heading text-lg leading-tight">{c.name}</div>
            <p className="text-xs text-gray-600 mt-1.5 line-clamp-2">{c.description}</p>
            <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
              <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {c.enrolled_count} enrolled</span>
              <span className="flex items-center gap-1"><Send className="h-3 w-3" /> {c.sent_count} sent</span>
              <span className="uppercase-tiny">{c.steps?.length || 0} steps</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
